import {useAdjustRuntime} from '@domain/running-times'
import {usePlantConfig} from '@domain/shifthandover'
import {TimeFormat} from '@hconnect/common/components'
import {RcfaLinks} from '@hconnect/common/components/runningTimes'
import {
  DateRange,
  EquipmentData,
  EquipmentRunningTimesDto as EquipmentRunningTimes,
  RunningTime,
  RunningTimeDto,
  RunningTimeStoppage,
  TimeZone
} from '@hconnect/common/types'
import {CardBox} from '@hconnect/uikit/src/lib2'
import {spawnErrorToast, spawnSuccessToast} from '@hm/fundamental'
import {FeatureFlagKey, useCommonFeatureFlag} from '@hproduce/feature-flags'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import {Box, Button, Grid, Theme, Typography, useMediaQuery} from '@mui/material'
import {StoppageSplitDateTimeEditor} from '@widgets/common-running-times'
import {
  getEquipmentStoppageCodesByStoppageType,
  getMaxEditableBarCount,
  getRunningTimesWithGap,
  type OnDragEvent,
  StackedBarChart
} from '@widgets/running-times'
import {throttle} from 'lodash'
import {Moment} from 'moment-timezone'
import {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {
  RunningTimeEditInfoLabel,
  SplitRuntimeDialog,
  StoppageAnalysisGraphActions,
  StoppageAnalysisGraphLabels
} from '../components'
import {FEATURE_NAMESPACE} from '../consts'
import {
  getRunningTimeToUpdate,
  getTemporaryStoppageSplitId,
  getZoomedAndFilteredData,
  getZoomedDateRange,
  prepareRunningTimesForChart,
  updateRunningTimesWithNewDate
} from '../utils'

const EDIT_RUNTIMES_LIMIT_MULTIPLIER = 4

type StoppageAnalysisGraphProps = {
  plantId: string
  equipmentId: string
  data: EquipmentRunningTimes[]
  setIsMaxEditableBarCountExceeded: (isCountExceeded: boolean) => void
  equipmentIdToPreview?: string
  onEquipmentChange: (id: string) => void
  handleStoppageClick: (
    rt: RunningTimeDto,
    equipment?: EquipmentData,
    stoppage?: RunningTimeStoppage
  ) => void
  timezone: TimeZone
  timeRange: DateRange
  onDragZoom: (range: DateRange) => void
  onZoomReset: (id?: string) => void
  isAdjustMode: boolean
  disableResetZoom: boolean
  onEditCancel: () => void
}

export const StoppageAnalysisGraph: FC<StoppageAnalysisGraphProps> = ({
  plantId,
  equipmentId,
  data,
  equipmentIdToPreview = '',
  onEquipmentChange,
  handleStoppageClick,
  onDragZoom,
  onZoomReset,
  timeRange,
  timezone,
  setIsMaxEditableBarCountExceeded,
  isAdjustMode,
  disableResetZoom,
  onEditCancel
}) => {
  const {t, i18n} = useTranslation(FEATURE_NAMESPACE)
  const isStoppageAnalysisGraphEditMode = useCommonFeatureFlag(
    FeatureFlagKey.StoppageAnalysisGraphEditMode
  )
  const [dynamicLabelRef, setDynamicLabelRef] = useState<HTMLDivElement>()
  const endDateRef = useRef<HTMLDivElement>()
  const startDateRef = useRef<HTMLDivElement>()
  const {data: config} = usePlantConfig({plantId, language: i18n.language})
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))
  const equipmentWithRunningTimes = data.find((eq) => eq.equipment?.id === equipmentId)
  const equipmentRunningTimes = equipmentWithRunningTimes?.runningTimes
  const [runningTimesWithGaps, setRunningTimesWithGaps] = useState<RunningTime[] | undefined>()
  const [runningTimeToSplit, setRunningTimeToSplit] = useState<RunningTimeDto | undefined>(
    undefined
  )

  const stoppageCodesByType = useMemo(
    () => getEquipmentStoppageCodesByStoppageType(config),
    [config]
  )

  const {mutate: adjustRuntime, isDelayedLoading: isAdjustingRuntime} = useAdjustRuntime(plantId, {
    onSuccess: () => {
      spawnSuccessToast(t('splitRuntime.message.adjustRuntimeSuccess'))
    },
    onError: () => {
      setRunningTimesWithGaps(
        equipmentRunningTimes && getRunningTimesWithGap(equipmentRunningTimes, timeRange, timezone)
      )
      spawnErrorToast(t('splitRuntime.message.adjustRuntimeError'))
    }
  })

  const throttledAdjustRuntime = useMemo(
    () =>
      throttle((runningTimeId: number, begin: Moment, end?: Moment) => {
        adjustRuntime({runningTimeId, start: begin.toISOString(), end: end?.toISOString()})
      }, 500),
    [adjustRuntime]
  )

  useEffect(() => {
    setRunningTimesWithGaps(
      equipmentRunningTimes && getRunningTimesWithGap(equipmentRunningTimes, timeRange, timezone)
    )
  }, [equipmentId, equipmentRunningTimes, timeRange, timezone, isAdjustMode])

  const handleSetDynamicLabelRef = useCallback((node: HTMLDivElement | null) => {
    if (node !== null) {
      setDynamicLabelRef(node)
    }
  }, [])

  const overflowsEndDate = useMemo(() => {
    const right = dynamicLabelRef?.getBoundingClientRect().right
    const left = endDateRef.current?.getBoundingClientRect().left
    if (right && left && data && timeRange) {
      return right > left
    }
    return true
  }, [data, timeRange, dynamicLabelRef, endDateRef])

  const overflowsStartDate = useMemo(() => {
    const left = dynamicLabelRef?.getBoundingClientRect().left
    const right = startDateRef.current?.getBoundingClientRect().right
    if (right && left && data && timeRange) {
      return right > left
    }
    return false
  }, [data, timeRange, dynamicLabelRef])

  const handleResize: OnDragEvent = useCallback(
    ({percentEnd, totalRunningTimeMs, index}) => {
      if (index === undefined) return
      const offsetMs = percentEnd * totalRunningTimeMs
      const newDate = timeRange.startDate.clone().add(offsetMs, 'milliseconds')
      setRunningTimesWithGaps((rt) => updateRunningTimesWithNewDate(index, rt, newDate))
    },
    [timeRange]
  )

  const handleSaveResize: OnDragEvent = useCallback(
    ({index}) => {
      if (index === undefined) return
      const runTime = getRunningTimeToUpdate(index, runningTimesWithGaps)
      if (runTime && runTime.id && runTime.begin && runTime.end)
        adjustRuntime({
          runningTimeId: runTime.id,
          start: runTime.begin?.toISOString(),
          end: runTime.endTechnical && runTime.end?.toISOString()
        })
    },
    [adjustRuntime, runningTimesWithGaps]
  )

  const handleDragZoom: OnDragEvent = useCallback(
    ({percentStart, percentEnd, totalRunningTimeMs}) => {
      const zoomedDateRange = getZoomedDateRange({
        percentStart: percentStart,
        percentEnd: percentEnd,
        totalRunningTimeMs,
        zoomedRange: timeRange,
        minGranularity: 'hours'
      })
      const filteredData = getZoomedAndFilteredData({
        equipmentRunningTimes,
        zoomedDateRange,
        timezone
      })
      if (filteredData?.length) {
        onDragZoom?.(zoomedDateRange)
      }
    },
    [equipmentRunningTimes, onDragZoom, timeRange, timezone]
  )

  const handleBarChartResize = useCallback(
    (data: RunningTime[], width?: number) => {
      const maxEditableBarCount = width ? getMaxEditableBarCount(width) : 0
      const isMaxEditableBarCountExceeded =
        data.length * EDIT_RUNTIMES_LIMIT_MULTIPLIER > maxEditableBarCount
      setIsMaxEditableBarCountExceeded(isMaxEditableBarCountExceeded)
    },
    [setIsMaxEditableBarCountExceeded]
  )

  const setSplitDate = useCallback(
    (newDate: Moment, id?: number) => {
      if (id === undefined || !newDate.isValid()) return
      setRunningTimesWithGaps((runTimes) => {
        const index = runTimes?.findIndex((rt) => rt.id === id)
        if (index === undefined) return runTimes
        const updatedRuntimes = updateRunningTimesWithNewDate(index, runTimes, newDate)
        const runTime = getRunningTimeToUpdate(index, updatedRuntimes)
        if (runTime && runTime.id && runTime.begin && runTime.end) {
          throttledAdjustRuntime(runTime.id, runTime.begin, runTime.endTechnical && runTime.end)
        }
        return updatedRuntimes
      })
    },
    [throttledAdjustRuntime]
  )

  const onSplitRunningTime = useCallback(
    (runningTimes: [RunningTime, RunningTime, RunningTime]) => {
      setRunningTimesWithGaps((rtWithGaps) => {
        const index = rtWithGaps?.findIndex(
          (rt) => getTemporaryStoppageSplitId(0, rt.id) === runningTimes[0].id
        )
        if (index === undefined || index === -1 || !rtWithGaps) return rtWithGaps
        const newRunningTimes = prepareRunningTimesForChart(runningTimes, timeRange)
        const nextRunningTimesWithGaps = [...rtWithGaps]
        nextRunningTimesWithGaps.splice(index, 1, ...newRunningTimes)
        return nextRunningTimesWithGaps
      })
    },
    [timeRange]
  )

  const stoppageClickHandler = useMemo(() => {
    if (isAdjustMode) {
      if (isStoppageAnalysisGraphEditMode) {
        return (rt: RunningTimeDto, stoppage?: RunningTimeStoppage) => {
          if (!stoppage) setRunningTimeToSplit(rt)
        }
      }
      return undefined
    }

    return (rt: RunningTimeDto, stoppage?: RunningTimeStoppage) =>
      handleStoppageClick(rt, equipmentWithRunningTimes?.equipment, stoppage)
  }, [
    isAdjustMode,
    isStoppageAnalysisGraphEditMode,
    equipmentWithRunningTimes,
    handleStoppageClick
  ])

  return (
    <CardBox>
      <Grid container spacing={3} alignItems="center">
        <StoppageAnalysisGraphActions
          plantId={plantId}
          equipmentId={equipmentId}
          onEquipmentChange={onEquipmentChange}
          equipmentIdToPreview={equipmentIdToPreview}
          onZoomReset={onZoomReset}
          data={data}
          disableEquipmentChange={isAdjustMode}
          disableResetZoom={disableResetZoom}
        />
        <Grid item xs={12} data-test-id="stoppage-analysis-graph-label">
          {equipmentWithRunningTimes && runningTimesWithGaps && (
            <>
              <StoppageAnalysisGraphLabels
                timeRange={timeRange}
                timezone={timezone}
                startDateRef={startDateRef}
                overflowsStartDate={overflowsStartDate}
                overflowsEndDate={overflowsEndDate}
                endDateRef={endDateRef}
                equipmentRunningTimes={equipmentRunningTimes}
              />
              <StackedBarChart
                sx={{height: 48, borderRadius: isAdjustMode ? 0.75 : 0}}
                onResize={handleBarChartResize}
                timeRange={timeRange}
                isLoading={isAdjustingRuntime}
                timezone={timezone}
                getResizeHandleTooltip={(rt, nextRt) =>
                  rt.beginTechnical &&
                  rt.endTechnical && (
                    <StoppageSplitDateTimeEditor
                      stoppageTimeRange={[rt.begin || rt.beginTechnical, nextRt?.end]}
                      timezone={timezone}
                      splitDate={rt.endTechnical}
                      setSplitDate={(newDate) => setSplitDate(newDate, rt.id)}
                      tooltipHeader={t('label.adjustAt')}
                    />
                  )
                }
                isEditMode={isAdjustMode}
                showDraggableArea={!isAdjustMode}
                onDragEnd={
                  !isMobile ? (isAdjustMode ? handleSaveResize : handleDragZoom) : undefined
                }
                onMove={!isMobile && isAdjustMode ? handleResize : undefined}
                data={runningTimesWithGaps}
                handleStoppageClick={stoppageClickHandler}
                handleRunningClick={
                  isAdjustMode && isStoppageAnalysisGraphEditMode
                    ? (rt) => setRunningTimeToSplit(rt)
                    : undefined
                }
                equipment={equipmentWithRunningTimes.equipment as EquipmentData}
                stoppageCodesByType={stoppageCodesByType}
                renderTooltipFooter={({rootCauseFailureAnalyses}) =>
                  rootCauseFailureAnalyses && (
                    <RcfaLinks
                      plantId={plantId}
                      rootCauseFailureAnalyses={rootCauseFailureAnalyses}
                    />
                  )
                }
                aboveBarComponent={(rt) => (
                  <Box
                    component={Typography}
                    fontWeight={600}
                    fontSize={12}
                    lineHeight="16px"
                    mt={-3}
                    ref={handleSetDynamicLabelRef}
                    sx={{transform: 'translateX(-50%)'}}
                    visibility={overflowsEndDate || overflowsStartDate ? 'hidden' : 'visible'}
                    position="absolute"
                  >
                    {t('stoppageAnalysisGraph.label.now')},{' '}
                    {rt.begin && <TimeFormat date={rt.begin} timezone={timezone} />}
                  </Box>
                )}
              />
              <RunningTimeEditInfoLabel isAdjustMode={isAdjustMode} />
              {isAdjustMode && (
                <Box display="flex" justifyContent="flex-end" gap={1.5} mt={2}>
                  <Button
                    startIcon={<CloseOutlinedIcon />}
                    variant="text"
                    onClick={onEditCancel}
                    data-test-id="stoppage-analysis-edit-cancel-button"
                  >
                    {t('stoppageAnalysis.action.cancel')}
                  </Button>
                </Box>
              )}
            </>
          )}
        </Grid>
      </Grid>
      {runningTimeToSplit && (
        <SplitRuntimeDialog
          plantId={plantId}
          runningTime={runningTimeToSplit}
          timezone={timezone}
          stoppageCodesByType={stoppageCodesByType}
          onClose={() => setRunningTimeToSplit(undefined)}
          onSubmit={onSplitRunningTime}
        />
      )}
    </CardBox>
  )
}
